import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
  writeBatch,
  type DocumentReference,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import type { InvestmentChatMessage, InvestmentChatThread } from '../types';
import { db } from '../lib/firebase';
import {
  COLLECTION_INVESTMENT_CHAT_MESSAGES,
  COLLECTION_INVESTMENT_CHAT_THREADS,
  ownedItemDocId,
} from '../lib/firestorePaths';
import { newId } from '../utils/id';

const FIRESTORE_BATCH_LIMIT = 500;

export type InvestmentChatThreadsState = {
  /** Recent-first. */
  threads: InvestmentChatThread[];
  /** Chronological messages of the active thread only. */
  messages: InvestmentChatMessage[];
  threadsLoading: boolean;
  messagesLoading: boolean;
  error: string | null;
  createThread: (title?: string) => Promise<InvestmentChatThread>;
  deleteThread: (threadId: string) => Promise<void>;
};

function docToMessage(d: QueryDocumentSnapshot): InvestmentChatMessage {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const parts = d.id.split('__');
  const fallbackId = parts.length > 1 ? parts.slice(1).join('__') : d.id;

  return {
    id: typeof x.id === 'string' && x.id.trim() ? x.id.trim() : fallbackId,
    userId: typeof x.userId === 'string' ? x.userId : '',
    companyId: typeof x.companyId === 'string' ? x.companyId : '',
    threadId: typeof x.threadId === 'string' ? x.threadId : '',
    role: x.role === 'assistant' ? 'assistant' : 'user',
    content: typeof x.content === 'string' ? x.content : '',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
  };
}

function docToThread(d: QueryDocumentSnapshot): InvestmentChatThread {
  const x = d.data() as Record<string, unknown>;
  const now = new Date().toISOString();
  const parts = d.id.split('__');
  const fallbackId = parts.length > 1 ? parts.slice(1).join('__') : d.id;

  return {
    id: typeof x.id === 'string' && x.id.trim() ? x.id.trim() : fallbackId,
    userId: typeof x.userId === 'string' ? x.userId : '',
    companyId: typeof x.companyId === 'string' ? x.companyId : '',
    title: typeof x.title === 'string' && x.title.trim() ? x.title.trim() : 'New chat',
    createdAt: typeof x.createdAt === 'string' ? x.createdAt : now,
    updatedAt: typeof x.updatedAt === 'string' ? x.updatedAt : now,
  };
}

/** Oldest messages first. */
export function sortInvestmentChatChronological(
  messages: InvestmentChatMessage[]
): InvestmentChatMessage[] {
  return [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Most recently updated threads first. */
export function sortInvestmentChatThreadsRecentFirst(
  threads: InvestmentChatThread[]
): InvestmentChatThread[] {
  return [...threads].sort((a, b) => {
    const byUpdated = b.updatedAt.localeCompare(a.updatedAt);
    if (byUpdated !== 0) return byUpdated;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

/**
 * Live threads + messages for one portfolio company. Messages are written
 * server-side by the callable; threads are created and deleted by the client.
 */
export function useInvestmentChatThreads(
  userId: string,
  companyId: string,
  activeThreadId: string | null
): InvestmentChatThreadsState {
  const [storedThreads, setStoredThreads] = useState<InvestmentChatThread[]>([]);
  const [allMessages, setAllMessages] = useState<InvestmentChatMessage[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(Boolean(userId && companyId));
  const [messagesLoading, setMessagesLoading] = useState(Boolean(userId && companyId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId || !companyId) {
      setStoredThreads([]);
      setThreadsLoading(false);
      setError(null);
      return;
    }

    setThreadsLoading(true);

    const threadsQuery = query(
      collection(db, COLLECTION_INVESTMENT_CHAT_THREADS),
      where('userId', '==', userId),
      where('companyId', '==', companyId)
    );

    const unsubscribe = onSnapshot(
      threadsQuery,
      (snap) => {
        setStoredThreads(snap.docs.map(docToThread));
        setThreadsLoading(false);
      },
      (e) => {
        setError(e.message);
        setThreadsLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, companyId]);

  useEffect(() => {
    if (!userId || !companyId) {
      setAllMessages([]);
      setMessagesLoading(false);
      return;
    }

    setMessagesLoading(true);

    const messagesQuery = query(
      collection(db, COLLECTION_INVESTMENT_CHAT_MESSAGES),
      where('userId', '==', userId),
      where('companyId', '==', companyId)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snap) => {
        setAllMessages(snap.docs.map(docToMessage));
        setMessagesLoading(false);
      },
      (e) => {
        setError(e.message);
        setMessagesLoading(false);
      }
    );

    return unsubscribe;
  }, [userId, companyId]);

  const threads = useMemo(
    () => sortInvestmentChatThreadsRecentFirst(storedThreads),
    [storedThreads]
  );

  const messages = useMemo(
    () =>
      sortInvestmentChatChronological(
        activeThreadId ? allMessages.filter((message) => message.threadId === activeThreadId) : []
      ),
    [activeThreadId, allMessages]
  );

  const createThread = useCallback(
    async (title = 'New chat'): Promise<InvestmentChatThread> => {
      if (!userId) {
        throw new Error('Sign in to start a chat');
      }

      const now = new Date().toISOString();
      const thread: InvestmentChatThread = {
        id: newId(),
        userId,
        companyId,
        title: title.trim() || 'New chat',
        createdAt: now,
        updatedAt: now,
      };

      await setDoc(
        doc(db, COLLECTION_INVESTMENT_CHAT_THREADS, ownedItemDocId(userId, thread.id)),
        thread
      );

      return thread;
    },
    [companyId, userId]
  );

  const deleteThread = useCallback(
    async (threadId: string): Promise<void> => {
      if (!userId) {
        throw new Error('Sign in to delete a chat');
      }

      const messagesQuery = query(
        collection(db, COLLECTION_INVESTMENT_CHAT_MESSAGES),
        where('userId', '==', userId),
        where('companyId', '==', companyId),
        where('threadId', '==', threadId)
      );

      const messageSnap = await getDocs(messagesQuery);
      const refsToDelete: DocumentReference[] = [
        ...messageSnap.docs.map((messageDoc) => messageDoc.ref),
        doc(db, COLLECTION_INVESTMENT_CHAT_THREADS, ownedItemDocId(userId, threadId)),
      ];

      for (let i = 0; i < refsToDelete.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = writeBatch(db);
        for (const ref of refsToDelete.slice(i, i + FIRESTORE_BATCH_LIMIT)) {
          batch.delete(ref);
        }
        await batch.commit();
      }
    },
    [companyId, userId]
  );

  return useMemo(
    () => ({
      threads,
      messages,
      threadsLoading,
      messagesLoading,
      error,
      createThread,
      deleteThread,
    }),
    [threads, messages, threadsLoading, messagesLoading, error, createThread, deleteThread]
  );
}
